<template>
  <div
    :class="[
      'app-avatar-component',
      {
        'app-avatar-component--clickable': isClickable
      },
      props.class
    ]"
    :style="containerStyle"
    @click="handleClick"
  >
    <!-- 头像图片 -->
    <img
      v-if="props.src"
      class="app-avatar-component__image"
      :src="props.src"
      :style="avatarStyle"
      @error="handleImageError"
    />

    <!-- V 认证标识 -->
    <img
      v-if="currentAccountLevel && badgeImageSrc && isShowBadgeFlag"
      class="app-avatar-component__badge"
      :src="badgeImageSrc"
      :style="badgeStyle"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'
  import { AccountLevelImage } from '@/utils/constants'
  import type { AvatarProps } from './types'
  import { AVATAR_SIZE_MAP } from './types'

  // 默认头像路径，请根据实际项目路径调整
  const defaultAvatar = 'https://static.zaohaowu.com/jjewelry/web/images/avatar/user-HMQoCE-2.png'

  defineOptions({
    name: 'AppAvatar'
  })

  const props = withDefaults(defineProps<AvatarProps>(), {
    size: 'medium',
    clickable: true,
    isShowBadgeFlag: true
  })

  const router = useRouter()

  /**
   * 规范化尺寸值
   * 如果是数字，添加 px 单位；如果是字符串，直接使用
   */
  const normalizeSizeValue = (value: number | string | undefined): string => {
    if (value === undefined) return '0px'
    if (typeof value === 'number' || !Number.isNaN(Number(value))) {
      return `${value}px`
    }
    return value
  }

  /**
   * 当前的账号等级
   * 优先使用 accountLevel prop，其次从 tags 中解析
   */
  const currentAccountLevel = computed(() => {
    if (props.accountLevel) {
      return props.accountLevel
    }
    // return getAccountLevelFromTags(props.tags)
    return props.tags?.[0]
  })

  /**
   * V 认证标识图片源
   */
  const badgeImageSrc = computed(() => {
    if (!currentAccountLevel.value) return null
    return AccountLevelImage[currentAccountLevel.value]
  })

  /**
   * 计算实际的头像尺寸
   */
  const actualAvatarSize = computed(() => {
    if (props.avatarSize) {
      return props.avatarSize
    }
    return AVATAR_SIZE_MAP[props.size].avatar
  })

  /**
   * 计算实际的标识尺寸
   */
  const actualBadgeSize = computed(() => {
    if (props.badgeSize) {
      return props.badgeSize
    }
    return AVATAR_SIZE_MAP[props.size].badge
  })

  /**
   * 容器样式
   */
  const containerStyle = computed(() => {
    const size = normalizeSizeValue(actualAvatarSize.value)
    return {
      width: size,
      height: size
    }
  })

  /**
   * 头像样式
   */
  const avatarStyle = computed(() => {
    const size = normalizeSizeValue(actualAvatarSize.value)
    return {
      width: size,
      height: size
    }
  })

  /**
   * 标识样式
   */
  const badgeStyle = computed(() => {
    const baseConfig = AVATAR_SIZE_MAP[props.size]
    const offsetRight = baseConfig.badgeOffsetRight
    const offsetBottom = baseConfig.badgeOffsetBottom

    const size = normalizeSizeValue(actualBadgeSize.value)
    return {
      width: size,
      height: size,
      right: `${offsetRight}px`,
      bottom: `${offsetBottom}px`
    }
  })

  /**
   * 是否可点击
   */
  const isClickable = computed(() => {
    return props.clickable && !!props.userId
  })

  /**
   * 处理点击事件
   */
  const handleClick = () => {
    if (!isClickable.value) return

    const url = props.enterFrom
      ? `/account/${props.userId}?enter_from=${props.enterFrom}`
      : `/account/${props.userId}`

    router.push(url)
  }

  /**
   * 处理图片加载错误
   */
  const handleImageError = (event: Event) => {
    const target = event.target as HTMLImageElement
    if (target.src !== defaultAvatar) {
      target.src = defaultAvatar
    }
  }
</script>

<style scoped lang="stylus">
  .app-avatar-component
    position: relative
    display: inline-block
    flex-shrink: 0
    box-sizing: content-box

    &--clickable
      cursor: pointer

      &:hover .app-avatar-component__image
        opacity: 0.85

    &__image
      width: 100%
      height: 100%
      border-radius: 50%
      object-fit: cover
      display: block
      transition: opacity 0.2s ease

    &__badge
      position: absolute
      z-index: 10
      display: block
</style>
